// The BillingImporter seam (Slice 6; slice-plan.md:86 "the billing inbox"; ADR-0023 decision 8;
// money.real_billing_import).
//
// Types + interface only; the real importer (cloud-bill/Stripe reads, connector polling) lands in
// Slice 7 behind `money.real_billing_import`. Application code calls this interface, never a
// provider SDK directly (app-engineering §3). Tenant-scoped by construction: `accountId` + `appId`
// are required positional arguments. ADMIN DATA NEVER CROSSES HERE: this seam returns
// `BillingInboxItem` (customer-facing minor units, model/money-view.ts), never the admin `MicroEur`
// cost ledger (money.ts:21-27; run-ledger.ts) - the two cost surfaces stay structurally separate
// (ADR-0023 decision 2b).
import { z } from "zod";
// ── the subscription standing vocabulary (Slice 7) ─────────────────────────────────────────────
/**
 * WHAT A SUBSCRIPTION ROW MEANS TO NIGHTCREW, as a closed set of five.
 *
 * `subscriptions.status` is deliberately free text mirroring the billing provider's own vocabulary
 * (`packages/db/src/schema/subscriptions.ts:4-5`; `model/billing.ts` `Subscription.status` is a bare
 * non-empty string). That is correct for a MIRROR column and useless for a DECISION, and Slice 6.5
 * recorded the cost: `apps/api/src/money/money.service.ts` became the first consumer of the table
 * and had to order by the furthest period end because there was no closed set to filter on. This
 * enum is that closed set. The provider string stays free text in the column; it is MAPPED here at
 * the boundary and never compared to a literal anywhere else.
 *
 * Every member is a state the pack names, and nothing else is a member:
 *   - `pending` .. a row exists at the provider and has never been live. Grants nothing. It is the
 *                  default-deny bucket (invariant 1: access is a granted state, never a default) and
 *                  the reason a not-yet-paying row cannot be read as `active` by omission.
 *   - `active` ... the ordinary paying state. `platform-operations.md:30` calls the metering-to-invoice
 *                  path "the happy path" and branches the rest of §4's lifecycle off it (`:32-37`).
 *   - `grace` .... the charge failed and smart retries are running. Work CONTINUES:
 *                  "a failed charge runs smart retries over a grace period, with in-product and
 *                  email nudges; IF IT STAYS UNPAID the account pauses"
 *                  (`platform-operations.md:33`, emphasis on the conditional). Grace is a distinct
 *                  standing precisely because service is still granted during it.
 *   - `paused` ... work stops, data is kept, one click restores it. Reached by staying unpaid past
 *                  grace (`platform-operations.md:33`) or by a downgrade past a limit
 *                  ("a downgrade pauses what is above the new limit", `:34`), and consistent with
 *                  D51's downgrade-pauses-never-deletes rule.
 *   - `ended` .... the subscription is over: cancelled, or closed with the account
 *                  ("closing the account exports everything, cancels via Stripe", D51). It prices no
 *                  future month and grants nothing.
 *
 * NOT A MEMBER: anything about a trial. `instrumentation-and-ingest.md:44` names "trial to paid" as
 * a METRIC to track and no decision in the pack creates a trial state, so inventing one here would
 * put a service-granting standing in the product on the strength of an analytics line. A provider
 * row that says `trialing` is REFUSED by name (see `mapProviderStatus`) rather than mapped, which
 * surfaces the question instead of answering it.
 */
export const SubscriptionStanding = z.enum(["pending", "active", "grace", "paused", "ended"]);
//# sourceMappingURL=money.js.map